import React, { useEffect, useState, useMemo } from "react";
import {
  Box, Paper, Typography, Grid, Button, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, TablePagination, TextField, Chip,
  Card, CardContent, Autocomplete, CircularProgress, IconButton
} from "@mui/material";
import { Search, Visibility, CheckCircle, Cancel } from "@mui/icons-material";
import { tdsApi } from "../../services/tdsApi";
import ExportButton from "../../components/ExportButton/ExportButton";
import RefreshButton from "../../components/RefreshButton/RefreshButton";
import { exportAsPDF, exportAsCSV, exportAsExcel } from "../../utils/importExport";

const TDS = () => {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalCount, setTotalCount] = useState(0);


  const emptyFilters = {
    name: "",
    deducteeName: "",
    pan: "",
    paymentType: "",
    section: "",
    fromDate: "",
    toDate: "",
    status: "",
  };

  const [filterInputs, setFilterInputs] = useState(emptyFilters);
  const [activeFilters, setActiveFilters] = useState(emptyFilters);

  const [tdsData, setTdsData] = useState([]);
  const [tdsLoading, setTdsLoading] = useState(false);
  const [tdsError, setTdsError] = useState("");

  const paymentTypes = ["Salary", "Professional", "Rent", "Contract", "Commission", "Interest", "Dividend", "Other"];
  const sections = ["194C", "194J", "194I", "195", "192", "193", "194A", "194H"];
  const statuses = ["Deposited", "Pending", "Overdue"];

  useEffect(() => {
    let cancelled = false;

    const fetchReport = async () => {
      setTdsLoading(true);
      try {
        const res = await tdsApi.getAssociateTds({
          page: page + 1,
          limit: rowsPerPage,
          startDate: activeFilters.fromDate,
          endDate: activeFilters.toDate,
          name: activeFilters.name,
        });

        const raw = res?.data || [];
        let mapped = raw.map(item => ({
          _id: item._id,
          deducteeName: item.associateName,
          pan: item.panNumber?.[0] || "-",
          amountPaid: item.totalAmount,
          tdsPercentage: item.tdsPercent,
          tdsAmount: item.totalTds,
          dateOfDeduction: "-",
          dateOfDeposit: "-",
          status: "-"
        }));

        // Frontend filters (Remaining fields that might not be in API yet)
        if (activeFilters.deducteeName) {
          mapped = mapped.filter(i =>
            i.deducteeName.toLowerCase().includes(activeFilters.deducteeName.toLowerCase())
          );
        }
        if (activeFilters.pan) {
          mapped = mapped.filter(i =>
            i.pan.toLowerCase().includes(activeFilters.pan.toLowerCase())
          );
        }
        if (activeFilters.status) {
          mapped = mapped.filter(i => i.status === activeFilters.status);
        }

        if (!cancelled) {
          setTdsData(mapped);
          setTotalCount(res?.pagination?.totalRecords || 0);
        }
      } catch (e) {
        if (!cancelled) setTdsError(e.message);
      } finally {
        if (!cancelled) setTdsLoading(false);
      }
    };

    fetchReport();
    return () => { cancelled = true; };
  }, [page, rowsPerPage, activeFilters]);

  const handleSearch = () => {
    setActiveFilters(filterInputs);
    setPage(0);
  };

  const handleReset = () => {
    setFilterInputs(emptyFilters);
    setActiveFilters(emptyFilters);
    setPage(0);
  };

  const totals = useMemo(() => {
    return tdsData.reduce((acc, curr) => ({
      amountPaid: acc.amountPaid + (Number(curr.amountPaid) || 0),
      tdsAmount: acc.tdsAmount + (Number(curr.tdsAmount) || 0),
    }), { amountPaid: 0, tdsAmount: 0 });
  }, [tdsData]);

  const formatCurrency = a =>
    `Rs. ${Number(a || 0).toLocaleString('en-IN')}`;

  const formatDate = d => d ? new Date(d).toLocaleDateString("en-IN") : "-";

  const buildExportRows = () => {
    const rows = tdsData.map((r, i) => ({
      "Sr": page * rowsPerPage + i + 1,
      "Name": r.deducteeName || "-",
      "PAN": r.pan || "-",
      "Amount": r.amountPaid || 0,
      "TDS%": r.tdsPercentage || 0,
      "TDS": r.tdsAmount || 0,
    }));

    if (tdsData.length > 0) {
      rows.push({
        "Sr": "",
        "Name": "",
        "PAN": "Total:",
        "Amount": totals.amountPaid || 0,
        "TDS%": "-",
        "TDS": totals.tdsAmount || 0,
      });
    }

    return rows;
  };

  const getExportFileName = () => `TDS_Report_${new Date().toLocaleDateString("en-IN").replace(/\//g, "-")}`;

  const handleExportPDF = () => {
    if (tdsData.length === 0) return;
    exportAsPDF({ rows: buildExportRows(), title: "TDS Report", fileNameBase: getExportFileName() });
  };

  const handleExportCSV = () => {
    if (tdsData.length === 0) return;
    exportAsCSV({ rows: buildExportRows(), fileNameBase: getExportFileName() });
  };

  const handleExportExcel = () => {
    if (tdsData.length === 0) return;
    exportAsExcel({ rows: buildExportRows(), sheetName: "TDS Report", fileNameBase: getExportFileName() });
  };

  const getStatusChip = status => {
    switch (status?.toLowerCase()) {
      case "deposited": return <Chip icon={<CheckCircle />} label="Deposited" color="success" size="small" />;
      case "pending": return <Chip icon={<Cancel />} label="Pending" color="error" size="small" />;
      case "overdue": return <Chip label="Overdue" color="warning" size="small" />;
      default: return <Chip label={status || "Unknown"} size="small" />;
    }
  };

  return (
    <Box p={2}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4" fontWeight="bold">TDS Report</Typography>
        <Box sx={{ display: 'flex', gap: 1.2, alignItems: 'center' }}>
          <ExportButton
            onExportPdf={handleExportPDF}
            onExportCsv={handleExportCSV}
            onExportExcel={handleExportExcel}
          />
          <RefreshButton
            onClick={() => setActiveFilters({ ...activeFilters })}
            loading={tdsLoading}
            sx={{
              fontWeight: 600,
              px: 2,
              fontSize: '15px',
              borderRadius: 2,
              height: { xs: 'auto', sm: 40 }
            }}
          />
        </Box>
      </Box>

      {/* Filters */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">

            <Grid item xs={12} sm={6} md={3}>
              <TextField fullWidth size="small" label="Search Name"
                placeholder="Search by associate name..."
                value={filterInputs.name}
                onChange={e => setFilterInputs(p => ({ ...p, name: e.target.value }))} />
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <TextField fullWidth size="small" label="PAN"
                value={filterInputs.pan}
                onChange={e => setFilterInputs(p => ({ ...p, pan: e.target.value }))} />
            </Grid>

            {/* <Grid item xs={12} sm={6} md={2}>
              <Autocomplete options={statuses} value={filterInputs.status}
                onChange={(e, v) => setFilterInputs(p => ({ ...p, status: v || "" }))}
                renderInput={(p) => <TextField {...p} label="Status" size="small" />} />
            </Grid> */}

            <Grid item xs={12} sm={6} md={2}>
              <TextField fullWidth size="small" type="date" label="From Date"
                InputLabelProps={{ shrink: true }}
                value={filterInputs.fromDate}
                onChange={e => setFilterInputs(p => ({ ...p, fromDate: e.target.value }))} />
            </Grid>

            <Grid item xs={12} sm={6} md={2}>
              <TextField fullWidth size="small" type="date" label="To Date"
                InputLabelProps={{ shrink: true }}
                value={filterInputs.toDate}
                onChange={e => setFilterInputs(p => ({ ...p, toDate: e.target.value }))} />
            </Grid>

            <Grid item xs={12} sm={12} md={3}>
              <Box display="flex" gap={1}>
                <Button variant="contained" startIcon={<Search />} onClick={handleSearch}>
                  Search
                </Button>

                <Button variant="outlined" onClick={handleReset}>
                  Reset
                </Button>
              </Box>
            </Grid>

          </Grid>
        </CardContent>
      </Card>

      {/* Table */}
      <Paper>
        <TableContainer>
          <Table stickyHeader>
            <TableHead>
              <TableRow>
                {["Sr", "Name", "PAN", "Amount", "TDS%", "TDS"]
                  .map(h => <TableCell key={h}><b>{h}</b></TableCell>)}
              </TableRow>
            </TableHead>
            <TableBody>
              {tdsLoading ? (
                <TableRow><TableCell colSpan={9} align="center"><CircularProgress /></TableCell></TableRow>
              ) : tdsError ? (
                <TableRow><TableCell colSpan={9} align="center" color="error">{tdsError}</TableCell></TableRow>
              ) : tdsData.length === 0 ? (
                <TableRow><TableCell colSpan={9} align="center">No Data</TableCell></TableRow>
              ) : (
                tdsData.map((r, i) => (
                  <TableRow key={r._id || i}>
                    <TableCell>{page * rowsPerPage + i + 1}</TableCell>
                    <TableCell>{r.deducteeName || "-"}</TableCell>
                    <TableCell>{r.pan || "-"}</TableCell>
                    <TableCell align="">{formatCurrency(r.amountPaid)}</TableCell>
                    <TableCell align="">{r.tdsPercentage ? `${r.tdsPercentage}%` : "-"}</TableCell>
                    <TableCell align="">{formatCurrency(r.tdsAmount)}</TableCell>
                    {/* <TableCell>{formatDate(r.dateOfDeduction)}</TableCell> */}
                    {/* <TableCell>{formatDate(r.dateOfDeposit)}</TableCell> */}
                    {/* <TableCell>{getStatusChip(r.status)}</TableCell> */}
                  </TableRow>
                ))
              )}
              {!tdsLoading && tdsData.length > 0 && (
                <TableRow sx={{ backgroundColor: 'grey.200' }}>
                  <TableCell colSpan={3} align="right"><b>Total:</b></TableCell>
                  <TableCell><b>{formatCurrency(totals.amountPaid)}</b></TableCell>
                  <TableCell>-</TableCell>
                  <TableCell><b>{formatCurrency(totals.tdsAmount)}</b></TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div"
          count={totalCount}
          page={page}
          rowsPerPage={rowsPerPage}
          onPageChange={(e, n) => setPage(n)}
          onRowsPerPageChange={e => { setRowsPerPage(+e.target.value); setPage(0); }}
        />
      </Paper>
    </Box>
  );
};

export default TDS;
